import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Download } from "lucide-react";
import { FamilyTreeCanvas } from "../components/family-tree/FamilyTreeCanvas";
import "../components/family-tree/family-tree.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function photoSrc(url) {
  if (!url) return null;
  return url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;
}

function initials(f, l) {
  return `${f?.[0] ?? ""}${l?.[0] ?? ""}`.toUpperCase();
}

function years(m) {
  const b = m.birthDate ? new Date(m.birthDate).getFullYear() : "";
  const d = m.deathDate ? new Date(m.deathDate).getFullYear() : "";
  if (!b && !d) return "";
  return `${b || "?"}–${d || ""}`;
}

export default function SharedFamilyTree() {
  const { token } = useParams();
  const [tree, setTree] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/family-tree/shared/${token}`)
      .then((r) => setTree(r.data))
      .catch(() => setError("This shared family tree link is not available."))
      .finally(() => setLoading(false));
  }, [token]);

  const selected = useMemo(() => tree?.members.find((m) => m.id === selectedId), [tree, selectedId]);

  function downloadCrest() {
    const svg = document.querySelector('[data-testid="tree-crest-card"] svg');
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const url = URL.createObjectURL(new Blob([data], { type: "image/svg+xml;charset=utf-8" }));
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 780;
      canvas.height = 1020;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `${(tree?.crest?.familyName || tree?.name || "family").replace(/\s+/g, "-")}-crest.png`;
      a.click();
    };
    img.src = url;
  }

  const hasCrest = tree?.crest && (tree.crest.crestPlacement !== "hidden");

  if (loading) {
    return (
      <main className="family-tree-feature">
        <div style={{ display: "grid", placeItems: "center", minHeight: "70vh", color: "#5f6772" }}>
          Loading family tree…
        </div>
      </main>
    );
  }

  if (error || !tree) {
    return (
      <main className="family-tree-feature">
        <div style={{ display: "grid", placeItems: "center", minHeight: "70vh", color: "#5f6772", textAlign: "center", padding: 24 }}>
          <div>
            <h2 style={{ fontFamily: "Georgia, serif", color: "#071b33", marginBottom: 8 }}>Link unavailable</h2>
            <p>{error || "This shared family tree link is not available."}</p>
          </div>
        </div>
      </main>
    );
  }

  const src = selected ? photoSrc(selected.photoUrl) : null;

  return (
    <main className="family-tree-feature">
      <div className="ft-shared-banner" data-testid="shared-banner">
        <div>
          <div className="ft-shared-title">{tree.name}</div>
          <div className="ft-shared-tag">Shared family tree · view only</div>
        </div>
        {hasCrest && (
          <button className="ft-shared-crest-dl" onClick={downloadCrest} data-testid="shared-download-crest">
            <Download size={16} /> Download crest
          </button>
        )}
      </div>

      <FamilyTreeCanvas tree={tree} onSelectMember={(id) => setSelectedId(id)} />

      {selected && (
        <aside className="member-drawer" data-testid="shared-member-profile">
          <button className="drawer-close" onClick={() => setSelectedId(null)} aria-label="Close" data-testid="shared-profile-close">
            ×
          </button>
          <div className="ft-profile-head">
            <div className="ft-profile-portrait">
              {src ? <img src={src} alt={`${selected.firstName} ${selected.lastName}`} /> : <span>{initials(selected.firstName, selected.lastName)}</span>}
            </div>
            <div className="ft-profile-name">{selected.preferredName || selected.firstName} {selected.lastName}</div>
            {years(selected) && <div className="ft-profile-years">{years(selected)}</div>}
          </div>
          {selected.favoriteQuote && <p className="ft-quote">“{selected.favoriteQuote}”</p>}
          {selected.lifeStory && (
            <div className="ft-story-block">
              <span className="ft-story-label">Life story</span>
              <p className="ft-profile-bio">{selected.lifeStory}</p>
            </div>
          )}
          {selected.biography && <p className="ft-profile-bio">{selected.biography}</p>}
          <button className="secondary" onClick={() => setSelectedId(null)}>Close</button>
        </aside>
      )}
    </main>
  );
}
